import axios from "../../utils/axios";
import { buildQueryStringList, addQueryStringList } from "../../utils/stringUtils";
import type { PagedResponse } from "../types/api";
import type { RiskAssessmentDTO } from "../types/riskAssessmentDtos";
import type { ActionDTO } from "../types/actionDtos";

const apiUrl = process.env.REACT_APP_HS_API_URL;

// GetById returns RiskAssessmentWithIncompleteActionsResponseDTO — a deep RA
// detail graph (hazard evaluations, controls, actions, history, incomplete
// actions). Deferred to its own typing slice; stays `any` for now.
const fetchRiskAssessment = (riskAssessmentId) =>
    axios.get(`${apiUrl}/RiskAssessment/${riskAssessmentId}`);

// AllHistory returns RiskAssessmentTemplateWithHistoriesResponseDTO — the RA
// template plus all historical occurrences. Deep; deferred, stays `any`.
const fetchRiskAssessmentWithHistory = (riskAssessmentId) =>
    axios.get(`${apiUrl}/RiskAssessment/AllHistory/${riskAssessmentId}`);

// Every action the assessment has raised, across all its occurrences.
const fetchRiskAssessmentActions = (riskAssessmentId) =>
    axios.get<ActionDTO[]>(`${apiUrl}/RiskAssessment/${riskAssessmentId}/Actions`);

const addRiskAssessment = (riskAssessment) =>
    axios.post<RiskAssessmentDTO>(`${apiUrl}/RiskAssessment`, riskAssessment);

const uploadRiskAssessment = ({
    file,
    title,
    description,
    siteExternalId,
    assignedEmployeeExternalId,
    frequency,
    startDate,
    onUploadProgress,
}) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);
    formData.append("description", description ?? "");
    formData.append("siteExternalId", siteExternalId);
    formData.append("assignedEmployeeExternalId", assignedEmployeeExternalId);
    formData.append("frequency", frequency);
    formData.append("startDate", startDate);

    return axios.post<RiskAssessmentDTO>(`${apiUrl}/RiskAssessment/Upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
    });
};

// Review a document-only RA by uploading a new version (a new occurrence on the same RA).
const uploadRiskAssessmentVersion = ({ riskAssessmentId, file, dueDate, onUploadProgress }) => {
    const formData = new FormData();
    formData.append("file", file);
    if (dueDate) formData.append("dueDate", dueDate);

    return axios.post<void>(`${apiUrl}/RiskAssessment/${riskAssessmentId}/UploadVersion`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
    });
};

const amendRiskAssessment = (riskAssessment) =>
    axios.patch<void>(`${apiUrl}/RiskAssessment/Amend`, riskAssessment);

const deleteRiskAssessment = ({ siteExternalId, riskAssessmentId }) =>
    axios.delete<void>(`${apiUrl}/RiskAssessment/${siteExternalId}/${riskAssessmentId}`);

const fetchRiskAssessmentsForSites = ({
    siteExternalIds,
    pageSize,
    pageNum,
    riskLevel = null,
    sortOrder,
    orderByColumn,
    assignees,
    filteredText,
    fromDate,
    toDate
}) => {
    let queryString = buildQueryStringList("siteExternalIds", siteExternalIds);
    const queryStringAssignees = addQueryStringList("assignees", assignees);

    if (queryStringAssignees !== null) queryString += queryStringAssignees;
    if (fromDate !== null) queryString += `&fromDate=${fromDate}`;
    if (toDate !== null) queryString += `&toDate=${toDate}`;
    if (riskLevel !== null) queryString += `&riskLevel=${riskLevel}`;
    if (filteredText) queryString += `&filteredText=${filteredText}`;

    return axios.get<PagedResponse<RiskAssessmentDTO>>(
        `${apiUrl}/RiskAssessment/Sites/${queryString}&pageNum=${pageNum}&pageSize=${pageSize}&sortOrder=${sortOrder}&orderByColumn=${orderByColumn}`
    );
};

export const downloadRiskAssessmentsForSite = async ({
    siteExternalIds,
    riskLevel = null,
    assignees,
    filteredText,
    fromDate,
    toDate
}) => {

    let queryString = buildQueryStringList("siteExternalIds", siteExternalIds);
    const queryStringAssignees = addQueryStringList("assignees", assignees);

    if (queryStringAssignees !== null) queryString += queryStringAssignees;
    if (fromDate !== null) queryString += `&fromDate=${fromDate}`;
    if (toDate !== null) queryString += `&toDate=${toDate}`;
    if (riskLevel !== null) queryString += `&riskLevel=${riskLevel}`;
    if (filteredText) queryString += `&filteredText=${filteredText}`;

    return axios.get<RiskAssessmentDTO[]>(
        `${apiUrl}/RiskAssessment/Sites/Download/${queryString}`
    );
};


const riskAssessmentService = {
    fetchRiskAssessment,
    fetchRiskAssessmentActions,
    addRiskAssessment,
    uploadRiskAssessment,
    uploadRiskAssessmentVersion,
    fetchRiskAssessmentsForSites,
    amendRiskAssessment,
    deleteRiskAssessment,
    fetchRiskAssessmentWithHistory,
};

export default riskAssessmentService;
